import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import db from "../../services/query";
import http from "../../services/axios";
import lbl from "../../data/Labels.json";
import { AuthContext } from "../../contexts/authContext";
import MainButton from "../common/MainButton";
import MainTitle from "../common/MainTitle";
import Img from "../common/Img";

function profileValue(value, emptyLabel = lbl.NotRegistered || "ثبت نشده") {
  const v = String(value || "").trim();
  if (!v || v === "—" || v.toLowerCase() === "null") return emptyLabel;
  return v;
}

function emailForInput(email) {
  const e = String(email || "").trim();
  if (!e || e.toLowerCase() === "null") return "";
  const bad = ["not available", "n/a", "na", "none", "-", "."];
  if (bad.includes(e.toLowerCase())) return "";
  return e;
}

const MyAccountProfile = () => {
  const { logOut } = useContext(AuthContext);
  const photoInputRef = useRef(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [photoMessage, setPhotoMessage] = useState("");
  const [photoSaving, setPhotoSaving] = useState(false);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await db.getAll("login");
      const profile = res?.data?.data?.[0] || null;
      setUser(profile);
      setEmail(emailForInput(profile?.user_email));
    } catch (_e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSaveEmail = async (e) => {
    e.preventDefault();
    setEmailSaving(true);
    setEmailMessage("");
    try {
      const { data } = await http.patch("/login/me", { email });
      if (data?.ok) {
        setEmailMessage(data.message || lbl.EmailSaved);
        await loadProfile();
        window.dispatchEvent(new Event("aria-auth-changed"));
      } else {
        setEmailMessage(data?.message || lbl.SaveFailed);
      }
    } catch (_err) {
      setEmailMessage(lbl.SaveFailed || "خطا در ذخیره ایمیل.");
    } finally {
      setEmailSaving(false);
    }
  };

  const handlePhotoPick = () => {
    if (photoInputRef.current) photoInputRef.current.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setPhotoSaving(true);
    setPhotoMessage("");
    try {
      const formData = new FormData();
      formData.append("teacher_photo", file);
      const { data } = await http.post("/login/me/photo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.ok) {
        setPhotoMessage(data.message || lbl.PhotoSaved);
        const updated = data?.data?.[0];
        if (updated) setUser(updated);
        else await loadProfile();
        window.dispatchEvent(new Event("aria-auth-changed"));
      } else {
        setPhotoMessage(data?.message || lbl.SaveFailed);
      }
    } catch (_err) {
      setPhotoMessage(lbl.SaveFailed || "خطا در ذخیره عکس.");
    } finally {
      setPhotoSaving(false);
    }
  };

  const handleLogout = () => {
    if (typeof logOut === "function") logOut();
    else window.location.replace("/login.html");
  };

  if (loading) {
    return (
      <div className="main-content">
        <div className="main-content-body">
          <p>{lbl.Loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content my-account-profile">
      <div className="main-content-header">
        <div className="right-content">
          <MainButton title={lbl.PrevPage} type="back" />
          <MainTitle title={lbl.MyAccountProfile} />
        </div>
      </div>

      <div className="main-content-body">
        <section className="account-profile-hero">
          <div className="account-profile-identity">
            <div className="account-profile-avatar">
              <Img src={user?.photo} border={true} />
            </div>
            <div>
              <h2>{profileValue(user?.name)}</h2>
              <p className="account-profile-subtitle">{profileValue(user?.position)}</p>
              {user?.user_name ? (
                <p className="account-profile-meta ltr-value">{user.user_name}</p>
              ) : null}
              <button
                type="button"
                className="account-photo-btn"
                onClick={handlePhotoPick}
                disabled={photoSaving}
              >
                {photoSaving ? "..." : lbl.EditPhoto || "ویرایش عکس"}
              </button>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/svg+xml"
                hidden
                onChange={handlePhotoChange}
              />
              {photoMessage ? (
                <p className={`account-form-feedback ${photoMessage.includes("خطا") ? "error" : "ok"}`}>
                  {photoMessage}
                </p>
              ) : null}
            </div>
          </div>
        </section>

        <div className="account-profile-grid">
          <section className="account-profile-card">
            <h3>{lbl.JobInfo || "معلومات وظیفه"}</h3>
            <div className="account-info-grid">
              <div className="account-info-field">
                <span>{lbl.Role}</span>
                <div>{profileValue(user?.position)}</div>
              </div>
              <div className="account-info-field">
                <span>{lbl.Faculties}</span>
                <div>{profileValue(user?.faculty)}</div>
              </div>
              <div className="account-info-field">
                <span>{lbl.Department || "دیپارتمنت"}</span>
                <div>{profileValue(user?.department)}</div>
              </div>
              <div className="account-info-field">
                <span>{lbl.EmployeeId || "شماره کارمندی"}</span>
                <div className="ltr-value">{profileValue(user?.employee_id)}</div>
              </div>
              {Number(user?.position_id) === 1 && user?.employee_id ? (
                <div className="account-info-field">
                  <span>{lbl.MySalary || "معاش من"}</span>
                  <div>
                    <Link
                      to={`/mis/teachers/${user.employee_id}/profile/daily-salary?teacherName=${encodeURIComponent(
                        String(user?.name || "").trim()
                      )}`}
                    >
                      {lbl.DailyEarnings || "معاش امروز"}
                    </Link>
                    {" · "}
                    <Link
                      to={`/mis/teachers/${user.employee_id}/profile/salary?teacherName=${encodeURIComponent(
                        String(user?.name || "").trim()
                      )}`}
                    >
                      {lbl.Salary || "معاشات"}
                    </Link>
                  </div>
                </div>
              ) : null}
            </div>
          </section>

          <section className="account-profile-card">
            <h3>{lbl.ContactInfo || "اطلاعات تماس"}</h3>
            <div className="account-info-grid">
              <div className="account-info-field">
                <span>{lbl.Phone || "شماره تماس"}</span>
                <div className="ltr-value">{profileValue(user?.user_name)}</div>
              </div>
            </div>

            <form className="account-email-form" onSubmit={handleSaveEmail}>
              <label htmlFor="account_email">{lbl.Email || "ایمیل"}</label>
              <div className="account-email-row">
                <input
                  id="account_email"
                  type="email"
                  name="email"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  placeholder="name@example.com"
                  autoComplete="email"
                  dir="ltr"
                />
                <button type="submit" disabled={emailSaving}>
                  {emailSaving ? "..." : lbl.Save}
                </button>
              </div>
              {emailMessage ? (
                <p
                  className={`account-form-feedback ${
                    emailMessage.includes("خطا") ||
                    emailMessage.includes("اشتباه") ||
                    emailMessage.includes("درست")
                      ? "error"
                      : "ok"
                  }`}
                >
                  {emailMessage}
                </p>
              ) : null}
            </form>
          </section>
        </div>

        <section className="account-quick-links" aria-label={lbl.QuickLinks || "دسترسی سریع"}>
          <Link className="account-quick-link" to="/mis/change-password">
            <div className="title">{lbl.ChangePassword}</div>
            <div className="desc">{lbl.AccountSecurityHint || "رمز ورود به سیستم را به‌روز کنید"}</div>
          </Link>
          <button type="button" className="account-quick-link" onClick={handleLogout}>
            <div className="title">{lbl.Logout}</div>
            <div className="desc">{lbl.LogoutHint || "نشست جاری را ببندید و از حساب خارج شوید"}</div>
          </button>
        </section>
      </div>
    </div>
  );
};

export default MyAccountProfile;
